import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.snow.css';
import { quillOpsToHtml } from '../../../utils/quillContent';
import { stripMarketingEditorHtml } from '../../Marketing/email/marketingEmailHtmlFormatter';

interface QuillEditorProps {
  value: any;
  onChange: (value: string) => void;
  onChangeDelta?: (ops: any[]) => void;
  placeholder?: string;
  height?: string;
  readOnly?: boolean;
  className?: string;
  /** e.g. "Verdana, Geneva, sans-serif" for marketing emails */
  defaultFontFamily?: string;
  /** e.g. "14px" for normal body text */
  defaultFontSize?: string;
  /** Use Quill 2 getSemanticHTML() so lists/bold export correctly for email */
  useSemanticHtml?: boolean;
  /** Allow bold in toolbar/formats (signatures only). Marketing compose keeps this false. */
  allowBold?: boolean;
}

type UnprivilegedEditorLike = {
  getContents?: () => { ops?: any[] };
};

type QuillDelta = { ops?: Array<{ insert?: unknown; attributes?: Record<string, unknown> }> };

const PASTE_STRIP_TAGS = ['p', 'span', 'div', 'strong', 'b', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'] as const;

function stripInlineNodeStyles(node: HTMLElement) {
  if (!node?.style) return;
  if (node.style.color) node.style.removeProperty('color');
  if (node.style.fontWeight) node.style.removeProperty('font-weight');
}

function stripPasteDeltaAttributes(delta: QuillDelta): QuillDelta {
  if (!delta?.ops) return delta;
  return {
    ...delta,
    ops: delta.ops.map((op) => {
      if (!op.attributes) return op;
      const attrs = { ...op.attributes };
      delete attrs.bold;
      delete attrs.header;
      return Object.keys(attrs).length ? { ...op, attributes: attrs } : { insert: op.insert };
    }),
  };
}

function makePasteStripMatcher() {
  return (node: HTMLElement, delta: QuillDelta) => {
    stripInlineNodeStyles(node);
    return stripPasteDeltaAttributes(delta);
  };
}

function makeColorStripMatcher() {
  return (node: HTMLElement, delta: QuillDelta) => {
    stripInlineNodeStyles(node);
    return delta;
  };
}

type QuillTextChangeHandler = (delta: QuillDelta, oldDelta: unknown, source: string) => void;

type QuillLike = {
  getSelection: (focus?: boolean) => { index: number; length: number } | null;
  getFormat: (index?: number, length?: number) => Record<string, unknown>;
  getText: (index?: number, length?: number) => string;
  getLength: () => number;
  getContents: () => QuillDelta;
  setContents: (delta: QuillDelta, source?: string) => void;
  format: (name: string, value: unknown, source?: string) => void;
  formatText: (index: number, length: number, formats: string | Record<string, unknown>, source?: string) => void;
  on: (event: string, handler: QuillTextChangeHandler) => void;
  off: (event: string, handler: QuillTextChangeHandler) => void;
  root?: HTMLElement;
};

function stripBoldFromDelta(delta: QuillDelta): QuillDelta {
  if (!delta?.ops) return delta;
  return {
    ops: delta.ops.map((op) => {
      if (!op.attributes) return op;
      const attrs = { ...op.attributes };
      delete attrs.bold;
      delete attrs.header;
      return Object.keys(attrs).length ? { ...op, attributes: attrs } : { insert: op.insert };
    }),
  };
}

function deltaHasBold(delta: QuillDelta): boolean {
  return (delta.ops || []).some((op) => Boolean(op.attributes?.bold || op.attributes?.header));
}

/** Quill keeps bold "active" after paste/edit — new lines inherit it even when the previous line is normal. */
function clearBoldLeakOnNewLine(quill: QuillLike) {
  if (!isQuillReady(quill)) return;
  try {
    const sel = quill.getSelection(true);
    if (!sel) return;

    const idx = sel.index;
    if (idx < 1) return;

    let endOfPrevLine = idx - 1;
    while (endOfPrevLine > 0 && quill.getText(endOfPrevLine, 1) === '\n') {
      endOfPrevLine -= 1;
    }

    const prevBold = endOfPrevLine >= 0 ? Boolean(quill.getFormat(endOfPrevLine, 1).bold) : false;
    if (prevBold) return;

    quill.format('bold', false, 'silent');
    quill.format('header', false, 'silent');

    const lineBreak = quill.getText(idx).indexOf('\n');
    const lineLen = lineBreak === -1 ? Math.max(quill.getLength() - idx, 0) : lineBreak;
    if (lineLen > 0) {
      quill.formatText(idx, lineLen, { bold: false, header: false }, 'silent');
    }
  } catch {
    /* ignore */
  }
}

function unwrapPhantomBoldParagraphs(root: HTMLElement) {
  try {
    root.querySelectorAll('p, div').forEach((block) => {
      const elementKids = Array.from(block.children);
      if (elementKids.length !== 1) return;
      const only = elementKids[0];
      if (!(only instanceof HTMLElement)) return;
      if (only.tagName !== 'STRONG' && only.tagName !== 'B') return;
      while (only.firstChild) block.insertBefore(only.firstChild, only);
      only.remove();
    });

    root.querySelectorAll('strong, b').forEach((boldEl) => {
      if (!(boldEl instanceof HTMLElement)) return;
      if (!boldEl.querySelector('p, div, li, br')) return;
      const parent = boldEl.parentElement;
      if (!parent) return;
      while (boldEl.firstChild) parent.insertBefore(boldEl.firstChild, boldEl);
      boldEl.remove();
    });
  } catch {
    /* ignore */
  }
}

function removeAllBoldTags(root: HTMLElement) {
  root.querySelectorAll('strong, b').forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });
}

function stripUnwantedBoldFromQuill(quill: QuillLike, removeAllBold: boolean) {
  if (!isQuillReady(quill)) return;
  try {
    if (removeAllBold) {
      const contents = quill.getContents();
      if (deltaHasBold(contents)) {
        quill.setContents(stripBoldFromDelta(contents), 'silent');
      }
      if (quill.root) {
        unwrapPhantomBoldParagraphs(quill.root);
        removeAllBoldTags(quill.root);
      }
      quill.format('bold', false, 'silent');
      quill.format('header', false, 'silent');
      return;
    }

    const contents = quill.getContents();
    if (deltaHasBold(contents)) {
      quill.setContents(stripBoldFromDelta(contents), 'silent');
    }
    if (quill.root) unwrapPhantomBoldParagraphs(quill.root);
    quill.format('bold', false, 'silent');
    quill.format('header', false, 'silent');
  } catch {
    /* ignore */
  }
}

function sanitizeMarketingEditorDom(quill: QuillLike, allowBold: boolean) {
  if (!isQuillReady(quill)) return;
  try {
    clearBoldLeakOnNewLine(quill);
    stripUnwantedBoldFromQuill(quill, !allowBold);
  } catch {
    /* Quill 2 can throw Scroll.emit if DOM is mutated mid-update */
  }
}

function isQuillReady(quill: any): boolean {
  return Boolean(quill?.root && quill?.scroll);
}

function exportMarketingEditorHtml(
  quill: { root?: { innerHTML?: string } },
  removeAllBold: boolean
): string {
  return stripMarketingEditorHtml(quill.root?.innerHTML || '', removeAllBold);
}

/** Quill 2 Scroll.update crashes on many raw <div> trees — normalize before load. */
function prepareHtmlForQuill(html: string): string {
  let out = String(html || '').trim();
  if (!out) return '<p><br></p>';

  for (let i = 0; i < 5; i += 1) {
    const match = out.match(
      /^<div\b[^>]*style="[^"]*font-family:\s*(?:Verdana|Arial)[^"]*"[^>]*>([\s\S]*)<\/div>\s*$/i
    );
    if (!match) break;
    out = match[1].trim();
  }

  out = out.replace(/<div\b[^>]*class="[^"]*(?:gmail_quote|email-signature)[^"]*"[^>]*>/gi, '<p>');
  out = out.replace(/<div\b([^>]*)>/gi, '<p$1>');
  out = out.replace(/<\/div>/gi, '</p>');
  return out || '<p><br></p>';
}

/** QuillEditor expects HTML; coerce Quill delta objects so we never render "[object Object]". */
function normalizeEditorValue(value: unknown, prepareHtml: (html: string) => string): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return prepareHtml(value);
  if (typeof value === 'object') {
    const ops = Array.isArray(value) ? value : (value as { ops?: unknown[] }).ops;
    if (Array.isArray(ops)) return prepareHtml(quillOpsToHtml(ops as any));
  }
  return prepareHtml(String(value));
}

const QuillEditor: React.FC<QuillEditorProps> = ({ 
  value, 
  onChange, 
  onChangeDelta,
  placeholder = 'Start writing your content...',
  height = '300px',
  readOnly = false,
  className = '',
  defaultFontFamily,
  defaultFontSize = '14px',
  useSemanticHtml = false,
  allowBold: allowBoldProp,
}) => {
  const allowBold = allowBoldProp ?? !useSemanticHtml;
  const quillRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const themeObserverRef = useRef<MutationObserver | null>(null);
  const lastExternalValueRef = useRef<string>('');
  const isApplyingExternalValueRef = useRef(false);
  const userBoldIntentRef = useRef(false);
  const [editorHtml, setEditorHtml] = useState(() =>
    useSemanticHtml ? '' : normalizeEditorValue(value, (html) => html)
  );

  const deltaLooksEmpty = (delta: { ops?: Array<{ insert?: unknown }> } | null | undefined) => {
    const ops = delta?.ops || [];
    if (!ops.length) return true;
    if (ops.length === 1) {
      const insert = ops[0]?.insert;
      return insert === '\n' || insert === '';
    }
    return false;
  };

  const exportEditorHtml = useCallback((quill: { root?: { innerHTML?: string } }) => {
    return quill.root?.innerHTML || '';
  }, []);

  const loadHtmlIntoEditor = useCallback((html: string) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill || !isQuillReady(quill)) return false;

    const next = prepareHtmlForQuill(html);
    const hasText = next.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;

    try {
      if (typeof quill.clipboard?.convert === 'function') {
        const delta = quill.clipboard.convert({ html: next });
        if (hasText && deltaLooksEmpty(delta)) {
          // Avoid raw innerHTML assignment — it triggers Quill MutationObserver crashes.
          quill.setText(next.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() + '\n', 'silent');
        } else {
          quill.setContents(delta, 'silent');
        }
      } else {
        quill.setText(hasText ? next.replace(/<[^>]+>/g, ' ') : '\n', 'silent');
      }
      return true;
    } catch {
      try {
        quill.setText('\n', 'silent');
        return true;
      } catch {
        return false;
      }
    }
  }, []);

  const emitHtmlChange = useCallback(
    (html: string, editor?: UnprivilegedEditorLike) => {
      if (isApplyingExternalValueRef.current) return;
      const next = useSemanticHtml
        ? stripMarketingEditorHtml(html || '', !allowBold)
        : html || '';
      if (next === lastExternalValueRef.current) return;
      lastExternalValueRef.current = next;
      if (!useSemanticHtml) setEditorHtml(next);
      onChange(next);
      if (onChangeDelta && editor?.getContents) {
        const ops = editor.getContents()?.ops || [];
        onChangeDelta(ops);
      }
    },
    [onChange, onChangeDelta, useSemanticHtml, allowBold]
  );

  const readEditorHtml = useCallback(() => {
    const quill = quillRef.current?.getEditor?.() as QuillLike | undefined;
    if (!quill || !isQuillReady(quill)) return '';
    return useSemanticHtml
      ? exportMarketingEditorHtml(quill, !allowBold)
      : exportEditorHtml(quill) || '';
  }, [exportEditorHtml, useSemanticHtml, allowBold]);

  /** When parent loads a draft/template/signature, hydrate the editor. */
  useEffect(() => {
    const incoming = normalizeEditorValue(value, prepareHtmlForQuill);
    const stored = useSemanticHtml ? stripMarketingEditorHtml(incoming, !allowBold) : incoming;
    if (stored === lastExternalValueRef.current) return;

    lastExternalValueRef.current = stored;
    if (!useSemanticHtml) {
      setEditorHtml(incoming);
      return;
    }

    isApplyingExternalValueRef.current = true;

    let attempts = 0;
    const hydrate = () => {
      if (loadHtmlIntoEditor(incoming) || !value) {
        lastExternalValueRef.current = stored;
        queueMicrotask(() => {
          const quill = quillRef.current?.getEditor?.() as QuillLike | undefined;
          if (isQuillReady(quill)) {
            userBoldIntentRef.current = false;
            sanitizeMarketingEditorDom(quill!, allowBold);
          }
          isApplyingExternalValueRef.current = false;
        });
        return;
      }
      if (attempts++ < 40) {
        requestAnimationFrame(hydrate);
        return;
      }
      isApplyingExternalValueRef.current = false;
    };

    hydrate();
  }, [value, useSemanticHtml, allowBold, loadHtmlIntoEditor]);

  /** Reliable sync: ReactQuill onChange can miss updates in uncontrolled/defaultValue mode. */
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let attempts = 0;

    const attach = () => {
      const quill = quillRef.current?.getEditor?.() as QuillLike | undefined;
      if (!quill) return false;

      const onTextChange = (_delta: QuillDelta, _old: unknown, source: string) => {
        if (isApplyingExternalValueRef.current || source === 'silent') return;

        if (useSemanticHtml && source === 'user') {
          if (isQuillReady(quill)) {
            sanitizeMarketingEditorDom(quill, allowBold && userBoldIntentRef.current);
            emitHtmlChange(
              exportMarketingEditorHtml(quill, !allowBold),
              quill as UnprivilegedEditorLike
            );
          }
          return;
        }

        emitHtmlChange(readEditorHtml(), quill as UnprivilegedEditorLike);
      };

      quill.on('text-change', onTextChange);

      const onSelectionChange = () => {
        if (isApplyingExternalValueRef.current || !useSemanticHtml || allowBold) return;
        if (isQuillReady(quill)) {
          const before = exportMarketingEditorHtml(quill, true);
          sanitizeMarketingEditorDom(quill, false);
          const after = exportMarketingEditorHtml(quill, true);
          if (after !== before) {
            emitHtmlChange(after, quill as UnprivilegedEditorLike);
          }
        }
      };

      quill.on('selection-change', onSelectionChange);

      if (useSemanticHtml) {
        userBoldIntentRef.current = false;
        sanitizeMarketingEditorDom(quill, allowBold);
      }
      cleanup = () => {
        quill.off('text-change', onTextChange);
        quill.off('selection-change', onSelectionChange);
      };
      return true;
    };

    const tryAttach = () => {
      if (attach() || attempts++ >= 40) return;
      requestAnimationFrame(tryAttach);
    };

    tryAttach();

    return () => {
      cleanup?.();
    };
  }, [emitHtmlChange, readEditorHtml, useSemanticHtml, allowBold]);

  const modules = useMemo(() => {
    const pasteMatcher = useSemanticHtml ? makePasteStripMatcher() : makeColorStripMatcher();
    const matchers = PASTE_STRIP_TAGS.map((tag) => [tag, pasteMatcher] as const);

    const marketingToolbar = [
      ...(allowBold ? [['bold'] as const] : []),
      ['italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ];

    const defaultToolbar = [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      ['clean'],
    ];

    return {
      toolbar: {
        container: useSemanticHtml ? marketingToolbar : defaultToolbar,
        handlers:
          useSemanticHtml && allowBold
            ? {
                bold(this: { quill: QuillLike }, value: boolean) {
                  userBoldIntentRef.current = Boolean(value);
                  this.quill.format('bold', value);
                },
              }
            : undefined,
      },
      history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true,
      },
      clipboard: {
        matchVisual: false,
        matchers,
      },
    };
  }, [useSemanticHtml, allowBold]);

  const formats = useMemo(() => {
    if (useSemanticHtml) {
      const base = ['italic', 'underline', 'strike', 'list', 'bullet', 'link'] as const;
      return allowBold ? [...base, 'bold'] : [...base];
    }
    return [
      'header', 'bold', 'italic', 'underline', 'strike',
      'color', 'background', 'script',
      'blockquote', 'code-block',
      'list', 'bullet', 'indent', 'align',
      'link', 'image', 'video',
    ];
  }, [useSemanticHtml, allowBold]);

  const applyTheme = useCallback(() => {
    if (!containerRef.current) return;

    const isDark = document.documentElement.classList.contains('dark') || 
                   document.body.classList.contains('dark');
    
    const editor = containerRef.current.querySelector('.ql-editor') as HTMLElement;
    const toolbar = containerRef.current.querySelector('.ql-toolbar') as HTMLElement;
    const container = containerRef.current.querySelector('.ql-container') as HTMLElement;

    if (!editor || !toolbar || !container) return;

    // Apply smooth transitions
    [editor, toolbar, container].forEach(el => {
      el.style.transition = 'all 0.3s ease-in-out';
    });

    if (isDark) {
      // Dark theme styles
      toolbar.style.cssText = `
        background-color: #1f2937 !important;
        border-color: #374151 !important;
        color: #f9fafb !important;
        border-top-left-radius: 0.75rem;
        border-top-right-radius: 0.75rem;
        transition: all 0.3s ease-in-out;
      `;
      
      editor.style.cssText = `
        background-color: #111827 !important;
        color: #f9fafb !important;
        border-color: #374151 !important;
        min-height: ${height};
        font-weight: 400 !important;
        font-family: ${defaultFontFamily || 'Arial, Helvetica, sans-serif'} !important;
        font-size: ${defaultFontSize} !important;
        -webkit-font-smoothing: auto;
        transition: all 0.3s ease-in-out;
      `;
      
      // Set CSS custom properties for content styling
      editor.style.setProperty('--content-color', '#f9fafb');
      editor.style.setProperty('--content-bg', '#111827');

      container.style.cssText = `
        background-color: #111827 !important;
        border-color: #374151 !important;
        border-bottom-left-radius: 0.75rem;
        border-bottom-right-radius: 0.75rem;
        transition: all 0.3s ease-in-out;
      `;

      // Style toolbar buttons and icons
      const toolbarElements = toolbar.querySelectorAll(
        '.ql-picker-label, .ql-picker-item, button, .ql-stroke, .ql-fill'
      );
      
      toolbarElements.forEach((el) => {
        const element = el as HTMLElement;
        element.style.color = '#f9fafb';
        element.style.fill = '#f9fafb';
        element.style.stroke = '#f9fafb';
        element.style.transition = 'all 0.2s ease';
      });

      // Style dropdowns
      const dropdowns = toolbar.querySelectorAll('.ql-picker-options');
      dropdowns.forEach(dropdown => {
        const el = dropdown as HTMLElement;
        el.style.backgroundColor = '#1f2937';
        el.style.borderColor = '#374151';
      });

    } else {
      // Light theme styles
      toolbar.style.cssText = `
        background-color: #f8fafc !important;
        border-color: #e2e8f0 !important;
        color: #1e293b !important;
        border-top-left-radius: 0.75rem;
        border-top-right-radius: 0.75rem;
        transition: all 0.3s ease-in-out;
      `;
      
      editor.style.cssText = `
        background-color: #ffffff !important;
        color: #1e293b !important;
        border-color: #e2e8f0 !important;
        min-height: ${height};
        font-weight: 400 !important;
        font-family: ${defaultFontFamily || 'Arial, Helvetica, sans-serif'} !important;
        font-size: ${defaultFontSize} !important;
        -webkit-font-smoothing: auto;
        transition: all 0.3s ease-in-out;
      `;
      
      // Set CSS custom properties for content styling
      editor.style.setProperty('--content-color', '#1e293b');
      editor.style.setProperty('--content-bg', '#ffffff');

      container.style.cssText = `
        background-color: #ffffff !important;
        border-color: #e2e8f0 !important;
        border-bottom-left-radius: 0.75rem;
        border-bottom-right-radius: 0.75rem;
        transition: all 0.3s ease-in-out;
      `;

      // Style toolbar buttons and icons
      const toolbarElements = toolbar.querySelectorAll(
        '.ql-picker-label, .ql-picker-item, button, .ql-stroke, .ql-fill'
      );
      
      toolbarElements.forEach((el) => {
        const element = el as HTMLElement;
        element.style.color = '#1e293b';
        element.style.fill = '#1e293b';
        element.style.stroke = '#1e293b';
        element.style.transition = 'all 0.2s ease';
      });

      // Style dropdowns
      const dropdowns = toolbar.querySelectorAll('.ql-picker-options');
      dropdowns.forEach(dropdown => {
        const el = dropdown as HTMLElement;
        el.style.backgroundColor = '#ffffff';
        el.style.borderColor = '#e2e8f0';
      });
    }

    // Add hover effects to toolbar buttons
    const buttons = toolbar.querySelectorAll('button, .ql-picker-label');
    buttons.forEach(button => {
      const btn = button as HTMLElement;
      btn.addEventListener('mouseenter', () => {
        btn.style.backgroundColor = isDark ? '#374151' : '#e2e8f0';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.backgroundColor = 'transparent';
      });
    });
  }, [height, defaultFontFamily, defaultFontSize]);

  // Enhanced theme observer with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedApplyTheme = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(applyTheme, 100);
    };

    // Initial application
    debouncedApplyTheme();

    // Set up observers
    if (themeObserverRef.current) {
      themeObserverRef.current.disconnect();
    }

    themeObserverRef.current = new MutationObserver(debouncedApplyTheme);
    
    themeObserverRef.current.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    themeObserverRef.current.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });

    // Also listen for manual theme changes
    window.addEventListener('storage', debouncedApplyTheme);
    
    return () => {
      clearTimeout(timeoutId);
      if (themeObserverRef.current) {
        themeObserverRef.current.disconnect();
      }
      window.removeEventListener('storage', debouncedApplyTheme);
    };
  }, [applyTheme]);

  // Strip pasted bold/header formatting for marketing email editors.
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let attempts = 0;

    const attach = () => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return false;

      const onPaste = () => {
        if (!useSemanticHtml) return;

        const sel = quill.getSelection(true);
        const startIndex = sel?.index ?? 0;
        const replacedLength = sel?.length ?? 0;

        setTimeout(() => {
          if (!isQuillReady(quill)) return;
          try {
            userBoldIntentRef.current = false;
            const endIndex = quill.getSelection(true)?.index ?? startIndex;
            const insertedLength = Math.max(endIndex - startIndex, 0) + replacedLength;

            if (insertedLength > 0) {
              quill.formatText(startIndex, insertedLength, { bold: false, header: false }, 'silent');
            }
            sanitizeMarketingEditorDom(quill as QuillLike, allowBold && userBoldIntentRef.current);
            emitHtmlChange(
              exportMarketingEditorHtml(quill, !allowBold),
              quill as UnprivilegedEditorLike
            );
          } catch {
            /* ignore Quill paste race */
          }
        }, 0);
      };

      quill.on('paste', onPaste);
      cleanup = () => {
        quill.off('paste', onPaste);
      };
      return true;
    };

    const tryAttach = () => {
      if (attach() || attempts++ >= 40) return;
      requestAnimationFrame(tryAttach);
    };

    tryAttach();

    return () => {
      cleanup?.();
    };
  }, [useSemanticHtml, allowBold, emitHtmlChange, exportEditorHtml]);

  const customStyles = {
    minHeight: height,
    height: '100%',
  };

  const editorFontCss = defaultFontFamily
    ? `
          .quill-editor-wrapper .ql-editor,
          .quill-editor-wrapper .ql-editor p,
          .quill-editor-wrapper .ql-editor div,
          .quill-editor-wrapper .ql-editor span,
          .quill-editor-wrapper .ql-editor h1,
          .quill-editor-wrapper .ql-editor h2,
          .quill-editor-wrapper .ql-editor h3,
          .quill-editor-wrapper .ql-editor h4,
          .quill-editor-wrapper .ql-editor h5,
          .quill-editor-wrapper .ql-editor h6,
          .quill-editor-wrapper .ql-editor li,
          .quill-editor-wrapper .ql-editor blockquote {
            font-family: ${defaultFontFamily} !important;
          }`
    : '';

  return (
    <div 
      ref={containerRef}
      className={`quill-editor-wrapper ${useSemanticHtml ? 'marketing-email-editor' : ''} ${className}`}
      style={customStyles}
    >
      <style dangerouslySetInnerHTML={{
        __html: `
          .quill-editor-wrapper .ql-toolbar {
            border-radius: 0.75rem 0.75rem 0 0;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          }
          
          .quill-editor-wrapper .ql-container {
            border-radius: 0 0 0.75rem 0.75rem;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          }
          
          .marketing-email-editor .ql-editor,
          .marketing-email-editor .ql-editor p,
          .marketing-email-editor .ql-editor div,
          .marketing-email-editor .ql-editor span,
          .marketing-email-editor .ql-editor li {
            font-weight: 400 !important;
            font-synthesis: none;
          }

          .quill-editor-wrapper .ql-editor {
            font-size: ${defaultFontSize};
            line-height: 1.6;
            padding: 1rem;
            font-weight: 400 !important;
            ${defaultFontFamily ? `font-family: ${defaultFontFamily};` : ''}
          }
          ${editorFontCss}
          
          .quill-editor-wrapper .ql-editor:focus {
            outline: 2px solid #3b82f6;
            outline-offset: -2px;
          }
          
          .quill-editor-wrapper .ql-toolbar .ql-formats {
            margin-right: 1rem;
          }
          
          .quill-editor-wrapper .ql-toolbar button {
            padding: 4px 6px;
            margin: 0 1px;
            border-radius: 0.375rem;
            transition: all 0.2s ease;
          }
          
          .quill-editor-wrapper .ql-toolbar button:hover {
            transform: translateY(-1px);
          }
          
          .quill-editor-wrapper .ql-toolbar .ql-picker {
            border-radius: 0.375rem;
            transition: all 0.2s ease;
          }
          
          .quill-editor-wrapper .ql-snow .ql-tooltip {
            background-color: var(--tooltip-bg, #374151);
            color: var(--tooltip-color, #f9fafb);
            border-radius: 0.5rem;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
          
          /* Use neutral colors that work in both light and dark modes */
          .quill-editor-wrapper .ql-editor {
            color: var(--content-color, #1e293b) !important;
          }
          
          .quill-editor-wrapper .ql-editor * {
            color: inherit !important;
          }
          
          /* Override any hardcoded colors from pasted content */
          .quill-editor-wrapper .ql-editor p,
          .quill-editor-wrapper .ql-editor div,
          .quill-editor-wrapper .ql-editor span,
          .quill-editor-wrapper .ql-editor h1,
          .quill-editor-wrapper .ql-editor h2,
          .quill-editor-wrapper .ql-editor h3,
          .quill-editor-wrapper .ql-editor h4,
          .quill-editor-wrapper .ql-editor h5,
          .quill-editor-wrapper .ql-editor h6,
          .quill-editor-wrapper .ql-editor li,
          .quill-editor-wrapper .ql-editor blockquote {
            color: var(--content-color, #1e293b) !important;
          }

          .quill-editor-wrapper .ql-editor p {
            margin: 0;
            padding: 0;
            line-height: 1.6;
            min-height: 1.6em;
            font-weight: 400 !important;
          }

          .quill-editor-wrapper .ql-editor p,
          .quill-editor-wrapper .ql-editor div,
          .quill-editor-wrapper .ql-editor span,
          .quill-editor-wrapper .ql-editor li {
            font-weight: 400 !important;
          }

          .quill-editor-wrapper .ql-editor strong,
          .quill-editor-wrapper .ql-editor b {
            font-weight: 700 !important;
          }

          .quill-editor-wrapper .ql-editor h1,
          .quill-editor-wrapper .ql-editor h2,
          .quill-editor-wrapper .ql-editor h3,
          .quill-editor-wrapper .ql-editor h4,
          .quill-editor-wrapper .ql-editor h5,
          .quill-editor-wrapper .ql-editor h6 {
            margin: 0;
            padding: 0;
            line-height: 1.6;
            font-size: 14px;
            font-weight: 400;
          }

          .quill-editor-wrapper .ql-editor ol,
          .quill-editor-wrapper .ql-editor ul {
            margin: 0 0 12px;
            padding-left: 1.5em;
          }

          .quill-editor-wrapper .ql-editor li {
            margin-bottom: 4px;
          }

          /* Quill 2 renders bullets via data-list + CSS counters */
          .quill-editor-wrapper .ql-editor li[data-list='bullet'] > .ql-ui:before {
            content: '\\2022';
          }
          
          /* Ensure links are visible in both modes */
          .quill-editor-wrapper .ql-editor a {
            color: #3b82f6 !important;
          }
          
          /* Ensure code blocks are properly styled */
          .quill-editor-wrapper .ql-editor pre {
            background-color: var(--content-bg, #f8fafc) !important;
            color: var(--content-color, #1e293b) !important;
            border: 1px solid #e2e8f0 !important;
          }
          
          /* Ensure blockquotes are properly styled */
          .quill-editor-wrapper .ql-editor blockquote {
            border-left: 4px solid #3b82f6 !important;
            background-color: var(--content-bg, #f8fafc) !important;
            color: var(--content-color, #1e293b) !important;
          }
        `
      }} />
      
      <ReactQuill
        ref={quillRef}
        {...(useSemanticHtml ? { defaultValue: '' } : { value: editorHtml })}
        onChange={(content: string, _delta: unknown, _source: unknown, editor: UnprivilegedEditorLike) => {
          if (isApplyingExternalValueRef.current) return;
          const html = useSemanticHtml ? readEditorHtml() || content : content;
          emitHtmlChange(html, editor);
        }}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
        theme="snow"
        style={{ height: '100%', width: '100%' }}
      />
    </div>
  );
};

export default QuillEditor;